//third party imports
import { Injectable } from '@angular/core';
import { AccountInfo, AuthenticationResult } from '@azure/msal-browser';

//local imports
import { PinnedAccountInformationDto } from '../helpers';
import { securityLog } from '../helpers';
import { B2cAuthorityService } from './b2c-authority.service';
import { MsalStoragePolicyService } from './msal-storage-policy.service';
import { TenantClaimResolverService } from './tenant-claim-resolver.service';

@Injectable({
    providedIn: 'root'
})
export class AccountContextService {
    private readonly authorityStorageKeyPrefix = 'auth.account.authority';
    private readonly pinnedAccountStorageKey = 'auth.pinned-account';

    constructor(
        private authorityService: B2cAuthorityService,
        private storagePolicy: MsalStoragePolicyService,
        private tenantClaimResolver: TenantClaimResolverService,
    ) { }

    public resolveBestAccount(activeAccount: AccountInfo | null, accounts: AccountInfo[]): AccountInfo | undefined {
        if (activeAccount) {
            return activeAccount;
        }

        if (!accounts || accounts.length === 0) {
            return undefined;
        }

        if (accounts.length === 1) {
            return accounts[0];
        }

        securityLog.info('AccountContextService: Multiple cached accounts found and no active account is set, selecting most recently authenticated account.');

        const sortedAccounts = [...accounts].sort((a, b) => {
            const claimsA = a.idTokenClaims as Record<string, unknown> | undefined;
            const claimsB = b.idTokenClaims as Record<string, unknown> | undefined;

            const iatA = this.resolveNumericClaim(claimsA, 'iat');
            const iatB = this.resolveNumericClaim(claimsB, 'iat');
            if (iatA !== iatB) {
                return iatB - iatA;
            }

            const authTimeA = this.resolveNumericClaim(claimsA, 'auth_time');
            const authTimeB = this.resolveNumericClaim(claimsB, 'auth_time');
            return authTimeB - authTimeA;
        });

        return sortedAccounts[0];
    }

    public updateAuthContextFromResult(authenticationResult: AuthenticationResult, eventAuthority?: string): void {
        const account = authenticationResult.account;
        if (!account) {
            return;
        }

        const accountClaims = account.idTokenClaims as Record<string, unknown> | undefined;
        const tokenClaims = authenticationResult.idTokenClaims as Record<string, unknown> | undefined;
        const claims = tokenClaims || accountClaims;

        const authorityFromEvent = this.normalizeAuthority(eventAuthority);
        const authorityFromClaims = this.resolveAuthorityFromClaims(claims);
        const authorityToStore = authorityFromClaims || authorityFromEvent;

        this.updateAuthContextForAccount(account, authorityToStore);
    }

    public updateAuthContextForAccount(account: AccountInfo, authority?: string): void {
        if (!account) {
            return;
        }

        const normalizedAuthority = this.normalizeAuthority(authority) || this.resolveAuthorityForAccount(account);
        if (!normalizedAuthority) {
            return;
        }

        this.persistAuthorityForAccount(account, normalizedAuthority);
    }

    public resolveAuthorityForAccount(account: AccountInfo | undefined, fallbackAuthority?: string): string | undefined {
        if (!account) {
            return this.normalizeAuthority(fallbackAuthority);
        }

        const persisted = this.readPersistedAuthorityForAccount(account);
        if (persisted) {
            return persisted;
        }

        const claims = account.idTokenClaims as Record<string, unknown> | undefined;
        const fromClaims = this.resolveAuthorityFromClaims(claims);
        if (fromClaims) {
            return fromClaims;
        }

        return this.normalizeAuthority(fallbackAuthority);
    }

    public persistPinnedAccountInformationFromResult(authenticationResult: AuthenticationResult, eventAuthority?: string): PinnedAccountInformationDto | undefined {
        const account = authenticationResult.account;
        if (!account) {
            return undefined;
        }

        const accountClaims = account.idTokenClaims as Record<string, unknown> | undefined;
        const tokenClaims = authenticationResult.idTokenClaims as Record<string, unknown> | undefined;
        const claims = tokenClaims || accountClaims;

        return this.persistPinnedAccountInformation({
            homeAccountId: this.resolveAccountKey(account),
            authority: this.resolveAuthorityFromClaims(claims) || this.normalizeAuthority(eventAuthority) || this.resolveAuthorityForAccount(account) || '',
            tenant: this.tenantClaimResolver.resolveTenantFromClaims(claims) || '',
        });
    }

    public persistPinnedAccountInformation(pinned: PinnedAccountInformationDto): PinnedAccountInformationDto | undefined {
        const storage = this.storagePolicy.resolveConfiguredStorage();
        if (!storage) {
            return undefined;
        }

        const normalizedPinned = this.normalizePinnedAccountInformation(pinned);
        if (!normalizedPinned) {
            return undefined;
        }

        storage.setItem(this.pinnedAccountStorageKey, JSON.stringify(normalizedPinned));
        return normalizedPinned;
    }

    public readPinnedAccountInformation(): PinnedAccountInformationDto | undefined {
        const storage = this.storagePolicy.resolveConfiguredStorage();
        if (!storage) {
            return undefined;
        }

        const rawValue = storage.getItem(this.pinnedAccountStorageKey);
        if (!rawValue) {
            return undefined;
        }

        try {
            return this.normalizePinnedAccountInformation(JSON.parse(rawValue) as PinnedAccountInformationDto);
        } catch (error) {
            securityLog.warn('AccountContextService: Failed to parse pinned account information', error);
            return undefined;
        }
    }

    public clearPinnedAccountInformation(): void {
        const storage = this.storagePolicy.resolveConfiguredStorage();
        if (!storage) {
            return;
        }

        storage.removeItem(this.pinnedAccountStorageKey);
    }

    public resolvePreferredAccountForRefresh(pinned: PinnedAccountInformationDto | undefined, activeAccount: AccountInfo | null, accounts: AccountInfo[]): AccountInfo | undefined {
        if (!accounts || accounts.length === 0) {
            return undefined;
        }

        const pinnedHomeAccountId = (pinned?.homeAccountId || '').trim();
        if (pinnedHomeAccountId) {
            const exactMatch = accounts.find((account) => (account.homeAccountId || '').trim() === pinnedHomeAccountId);
            if (exactMatch) {
                return exactMatch;
            }
        }

        return activeAccount || undefined;
    }

    private resolveAuthorityFromClaims(claims: Record<string, unknown> | undefined): string | undefined {
        const policy = (
            this.tenantClaimResolver.resolveStringClaim(claims || {}, 'tfp')
            || this.tenantClaimResolver.resolveStringClaim(claims || {}, 'acr')
            || ''
        ).trim().toLowerCase();

        if (!policy) {
            return undefined;
        }

        const authorities = this.authorityService.getPolicyAuthorities();
        return this.normalizeAuthority(authorities[policy]);
    }

    private resolveNumericClaim(claims: Record<string, unknown> | undefined, claimName: string): number {
        if (!claims) {
            return 0;
        }

        const value = claims[claimName];
        if (typeof value === 'number' && Number.isFinite(value)) {
            return value;
        }

        if (typeof value === 'string') {
            const parsed = Number(value);
            if (Number.isFinite(parsed)) {
                return parsed;
            }
        }

        return 0;
    }

    private normalizeAuthority(authority: string | undefined): string | undefined {
        const normalized = (authority || '').trim().toLowerCase();
        return normalized.length > 0 ? normalized : undefined;
    }

    private persistAuthorityForAccount(account: AccountInfo, authority: string): void {
        const storage = this.storagePolicy.resolveConfiguredStorage();
        if (!storage) {
            return;
        }

        const accountKey = this.resolveAccountKey(account);
        if (!accountKey) {
            return;
        }

        storage.setItem(this.buildStorageKey(accountKey), authority);
    }

    private readPersistedAuthorityForAccount(account: AccountInfo): string | undefined {
        const storage = this.storagePolicy.resolveConfiguredStorage();
        if (!storage) {
            return undefined;
        }

        const accountKey = this.resolveAccountKey(account);
        if (!accountKey) {
            return undefined;
        }

        return this.normalizeAuthority(storage.getItem(this.buildStorageKey(accountKey)) || undefined);
    }

    private resolveAccountKey(account: AccountInfo): string {
        return (account.homeAccountId || account.localAccountId || account.username || '').trim();
    }

    private buildStorageKey(accountKey: string): string {
        return `${this.authorityStorageKeyPrefix}.${accountKey}`;
    }

    private normalizePinnedAccountInformation(pinned: PinnedAccountInformationDto | undefined): PinnedAccountInformationDto | undefined {
        const homeAccountId = (pinned?.homeAccountId || '').trim();
        if (!homeAccountId) {
            return undefined;
        }

        return {
            homeAccountId,
            authority: (pinned?.authority || '').trim().toLowerCase(),
            tenant: (pinned?.tenant || '').trim().toLowerCase(),
        };
    }
}
